import { useState } from "react";
import { useOrderManagement } from "@/hooks/use-order-management";
import { useUserOrganization } from "@/hooks/use-user-organization";
import { useRoleBasedAccess } from "@/hooks/use-role-based-access";
import { useOrderShipments } from "@/hooks/use-order-shipments";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, Package, Calendar, User, CreditCard, Building, CheckCircle, Truck } from "lucide-react";
import { OrderWorkflow } from "./OrderWorkflow";
import { ReservedCreditsBadge } from "@/components/devices/ReservedCreditsBadge";

// Component to show shipped devices count for an order
const ShippedDevicesCell = ({ 
  orderId, 
  orderType, 
  totalCameras 
}: { 
  orderId: string; 
  orderType?: string; 
  totalCameras: number;
}) => {
  const { data: shipments, isLoading } = useOrderShipments(orderId);
  
  // Credit-only orders don't have devices
  if (orderType === 'credits_only') {
    return <span className="text-muted-foreground">-</span>;
  }
  
  if (isLoading) {
    return <span className="text-muted-foreground">...</span>;
  }
  
  // Count devices from shipments that are shipped, delivered, or confirmed
  const shippedStatuses = ['shipped', 'delivered', 'confirmed'];
  const shippedDeviceCount = shipments
    ?.filter(s => shippedStatuses.includes(s.status))
    .reduce((sum, s) => sum + (s.allocated_device_ids?.length || 0), 0) || 0;
  
  // Determine color based on progress
  let colorClass = "text-muted-foreground"; // None shipped
  if (shippedDeviceCount > 0 && shippedDeviceCount < totalCameras) {
    colorClass = "text-amber-600"; // Partially shipped
  } else if (shippedDeviceCount >= totalCameras && totalCameras > 0) {
    colorClass = "text-green-600"; // Fully shipped
  }
  
  return (
    <div className={`flex items-center gap-1 ${colorClass}`}>
      <Truck className="h-4 w-4" />
      <span>{shippedDeviceCount}/{totalCameras}</span>
    </div>
  );
};

export const OrdersList = () => {
  const {
    data: orders,
    isLoading,
    error
  } = useOrderManagement();
  const {
    data: userOrganizationId,
    isLoading: isLoadingOrg
  } = useUserOrganization();
  const {
    isSystemAdmin
  } = useRoleBasedAccess();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  if (isLoading || isLoadingOrg) {
    return <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </CardContent>
      </Card>;
  }

  if (error) {
    return <Card>
        <CardContent className="p-8">
          <div className="text-center text-red-600">
            <p>Error loading orders. Please try again.</p>
          </div>
        </CardContent>
      </Card>;
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'waiting_for_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'order_processing':
        return 'bg-blue-100 text-blue-800';
      case 'in_fulfillment':
        return 'bg-purple-100 text-purple-800';
      case 'ready_for_shipping':
        return 'bg-indigo-100 text-indigo-800';
      case 'shipped':
        return 'bg-green-100 text-green-800';
      case 'delivered':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string, orderType?: string, fulfillmentStatus?: string) => {
    const isCreditOnly = orderType === 'credits_only';
    const isCompleted = fulfillmentStatus === 'completed';
    
    switch (status.toLowerCase()) {
      case 'waiting_for_payment':
        return 'Awaiting Payment';
      case 'order_processing':
        return isCreditOnly ? 'Processing Credits' : 'Processing';
      case 'in_fulfillment':
        return 'In Fulfillment';
      case 'ready_for_shipping':
        return 'Ready for Shipping';
      case 'shipped':
        return 'In Transit';
      case 'delivered':
        if (isCompleted) {
          return isCreditOnly ? 'Credits Delivered' : 'Completed';
        }
        return isCreditOnly ? 'Credits Delivered' : 'Awaiting Confirmation';
      default:
        return status.replace('_', ' ');
    }
  };

  const getOrderTypeIcon = (orderType?: string) => {
    return orderType === 'credits_only' ? <CreditCard className="h-4 w-4 text-blue-500" /> : <Package className="h-4 w-4 text-gray-400" />;
  };

  const getOrderTypeLabel = (orderType?: string) => {
    return orderType === 'credits_only' ? 'Credits Only' : 'Cameras & Credits';
  };

  // Filter orders for tabs - completed means fulfillment_status is 'completed'
  // This ensures multi-shipment orders stay in Active until all shipments are confirmed
  const isOrderCompleted = (order: typeof orders[0]) => {
    return order.fulfillment_status === 'completed';
  };

  const completedOrders = orders?.filter(isOrderCompleted) || [];
  const activeOrders = orders?.filter(order => !isOrderCompleted(order)) || [];

  const renderOrdersTable = (ordersToShow: typeof orders) => {
    if (!ordersToShow || ordersToShow.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No orders found</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Deal</TableHead>
              <TableHead>Cameras</TableHead>
              <TableHead>Shipped</TableHead>
              <TableHead>Credits</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordersToShow.map(order => (
              <TableRow key={order.id}>
                <TableCell className="font-mono text-sm">
                  {order.id.slice(0, 8)}...
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getOrderTypeIcon(order.order_type)}
                    <span className="text-sm">{getOrderTypeLabel(order.order_type)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="font-medium">
                        {order.sale?.deal?.contact?.first_name} {order.sale?.deal?.contact?.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {order.sale?.deal?.contact?.email}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {order.sale?.deal?.contact?.organization ? (
                    <div>
                      <p className="font-medium">{order.sale?.deal?.contact?.organization.name}</p>
                      <Badge variant="success" className="text-xs">
                        {order.sale?.deal?.contact?.organization.relationship_status}
                      </Badge>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">No organization</span>
                  )}
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{order.sale?.deal?.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {order.sale?.deal?.id.slice(0, 8)}...
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  {order.order_type === 'credits_only' ? (
                    <span className="text-gray-400">-</span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Package className="h-4 w-4 text-gray-400" />
                      {order.total_cameras}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <ShippedDevicesCell 
                    orderId={order.id} 
                    orderType={order.order_type} 
                    totalCameras={order.total_cameras} 
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <CreditCard className="h-4 w-4 text-gray-400" />
                      {order.total_images?.toLocaleString()}
                    </div>
                    <ReservedCreditsBadge orderId={order.id} />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    £{order.sale?.total_amount?.toLocaleString()}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(order.status)}>
                    {getStatusLabel(order.status, order.order_type, order.fulfillment_status)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {new Date(order.created_at).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedOrderId(order.id)} 
                    className="flex items-center gap-1"
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <>
      {/* Organization Context */}
      {(isSystemAdmin || userOrganizationId) && (
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building className="h-4 w-4" />
            {isSystemAdmin ? (
              <span>Viewing orders from <strong>all organizations</strong></span>
            ) : (
              <span>Viewing orders for organization: <span className="font-mono">{userOrganizationId}</span></span>
            )}
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Orders Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="active" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="active" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Active Orders ({activeOrders.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Completed ({completedOrders.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="active" className="mt-6">
              {renderOrdersTable(activeOrders)}
            </TabsContent>
            
            <TabsContent value="completed" className="mt-6">
              {renderOrdersTable(completedOrders)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Order Workflow Modal */}
      {selectedOrderId && (
        <OrderWorkflow 
          orderId={selectedOrderId} 
          open={!!selectedOrderId} 
          onOpenChange={open => !open && setSelectedOrderId(null)} 
        />
      )}
    </>
  );
};